import type { Dispatch, RefObject, SetStateAction } from 'react'
import { Keyboard } from 'types/keyboard'

import type { Position } from '../Tooltip'
import {
	type getBottomStyles,
	type getLeftStyles,
	type getRightStyles,
	type getTopStyles,
	getTriggerPositions,
	type TooltipArrowStyle,
	type TooltipContentStyle,
	type TooltipStyleResult,
} from './tooltipStyles'

interface OnTriggerChangeParams {
	boundary?: {
		current?: HTMLElement | null
	}
	setLimits: Dispatch<SetStateAction<{ left: number; right: number }>>
	minDistance: number
}

interface UpdateTooltipParams {
	show: boolean
	tooltipRef: RefObject<HTMLDivElement | null>
	triggerRef: RefObject<HTMLDivElement | null>
	position: Position
	offset: number
	limits: { left: number; right: number }
	positions: {
		bottom: typeof getBottomStyles
		top: typeof getTopStyles
		left: typeof getLeftStyles
		right: typeof getRightStyles
	}
	reverse: boolean
	setArrowStyles: Dispatch<SetStateAction<TooltipArrowStyle>>
	setTooltipStyles: Dispatch<SetStateAction<TooltipContentStyle>>
}

export function onTriggerChange({
	boundary,
	setLimits,
	minDistance,
}: OnTriggerChangeParams) {
	let left = minDistance
	let right = window.innerWidth - minDistance
	if (boundary?.current) {
		const clientRect = boundary.current.getBoundingClientRect()
		left = clientRect.left
		right = clientRect.right
	}
	setLimits({ left, right })
}

export function updateTooltipPosition({
	show,
	tooltipRef,
	triggerRef,
	position,
	offset,
	limits,
	positions,
	reverse,
	setArrowStyles,
	setTooltipStyles,
}: UpdateTooltipParams) {
	if (show && tooltipRef.current && triggerRef.current) {
		const tooltip = tooltipRef.current.getBoundingClientRect()
		const triggerRect = triggerRef.current.getBoundingClientRect()
		const { top, left, bottom, right } = getTriggerPositions(triggerRect)

		const trigger = {
			top,
			left,
			bottom,
			right,
			width: triggerRect.width,
			height: triggerRect.height,
		}

		const style: TooltipStyleResult = positions[position]({
			tooltip,
			trigger,
			offset,
			limits,
			reverse,
		})

		setArrowStyles(style.arrowStyles)
		setTooltipStyles(style.tooltipStyles)
	}
}

export function handleKeyDown({
	handleClose,
	handleOpen,
	e,
	show,
	hideOnKeyDown,
}: {
	handleClose: () => void
	handleOpen: () => void
	e: KeyboardEvent
	show: boolean
	hideOnKeyDown: boolean
}) {
	if (e.key === Keyboard.Enter || e.key === Keyboard.Space) {
		show && hideOnKeyDown ? handleClose() : handleOpen()
	} else if (e.key === Keyboard.Escape) {
		handleClose()
	} else {
		// Do nothing
	}
}
